import { DOCUMENT } from '@angular/common';
import {
  Attribute,
  Directive,
  ElementRef,
  HostBinding,
  HostListener,
  Inject,
  Input,
  Renderer2,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
  selector:
    '[appContentEditable][formControlName],[appContentEditable][formControl],[appContentEditable][ngModel]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: EditableDirective,
      multi: true,
    },
  ],
  standalone: false,
})
export class EditableDirective implements ControlValueAccessor {
  @Input() propValueAccessor = 'textContent';
  @HostBinding('attr.contenteditable') @Input('appContentEditable') editable: boolean | string =
    true;

  private onChange: (value: string) => void;
  private onTouched: () => void;
  private removeDisabledState: () => void;

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    @Attribute('unformattedPaste') private unformattedPaste: string,
    @Inject(DOCUMENT) private document: Document
  ) {}

  @HostListener('input')
  callOnChange() {
    if (typeof this.onChange === 'function') {
      this.onChange(this.elementRef.nativeElement[this.propValueAccessor]);
    }
  }

  @HostListener('blur')
  callOnTouched() {
    if (typeof this.onTouched === 'function') {
      this.onTouched();
    }
  }

  writeValue(value: any): void {
    const normalizedValue = value == null ? '' : value;
    this.renderer.setProperty(
      this.elementRef.nativeElement,
      this.propValueAccessor,
      normalizedValue
    );
  }

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.renderer.setAttribute(this.elementRef.nativeElement, 'disabled', 'true');
      this.removeDisabledState = this.renderer.listen(
        this.elementRef.nativeElement,
        'keydown',
        this.listenerDisabledState
      );
    } else {
      if (this.removeDisabledState) {
        this.renderer.removeAttribute(this.elementRef.nativeElement, 'disabled');
        this.removeDisabledState();
      }
    }
  }

  @HostListener('paste', ['$event'])
  preventFormatedPaste(event: ClipboardEvent) {
    if (
      this.unformattedPaste === null ||
      this.unformattedPaste === 'false' ||
      !this.document.execCommand
    ) {
      return;
    }
    event.preventDefault();
    const { clipboardData } = event;
    const text = clipboardData?.getData('text/plain') || clipboardData?.getData('text');
    this.document.execCommand('insertText', false, text);
  }

  private listenerDisabledState(e: KeyboardEvent) {
    e.preventDefault();
  }
}
